import { lazy } from "react";
import { Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import { AuthGuard } from "@/components/AuthGuard";
import AdminLayout from "@/components/AdminLayout";
import AdminUsersLayout from "@/components/AdminUsersLayout";
import NotFound from "@/pages/NotFound";
import OnboardingPage from "@/pages/onboarding/page";
import LoginPage from "@/pages/login/page";
import AdminUsersPage from "@/pages/admin-users/page";
import AdminUserDetailPage from "@/pages/admin-users/detail";
import SystemConfigPage from "@/pages/config/system-config";

function createCryptoRoutes(): RouteObject[] {
  const DashboardPage = lazy(() => import("@/pages/dashboard/page"));
  const UsersPage = lazy(() => import("@/pages/users/page"));
  const UserDetailPage = lazy(() => import("@/pages/users/detail"));
  const AgentsPage = lazy(() => import("@/pages/agents/page"));
  const AgentDetailPage = lazy(() => import("@/pages/agents/detail"));
  const SettlementsPage = lazy(() => import("@/pages/settlements/page"));
  const SettlementRecordsPage = lazy(() => import("@/pages/settlements/records"));
  const DepositsPage = lazy(() => import("@/pages/settlements/deposits"));
  const PaymentAddressesPage = lazy(() => import("@/pages/settlements/addresses"));
  const WithdrawalsPage = lazy(() => import("@/pages/withdrawals/page"));
  const WithdrawalHistoryPage = lazy(() => import("@/pages/withdrawals/history"));
  const RiskDashboardPage = lazy(() => import("@/pages/risk/dashboard"));
  const AuditLogsPage = lazy(() => import("@/pages/risk/audit-logs"));
  const ConfigRatesPage = lazy(() => import("@/pages/config/rates"));
  const ConfigPlansPage = lazy(() => import("@/pages/config/plans"));
  const ConfigAiModelsPage = lazy(() => import("@/pages/config/ai-models"));
  const ConfigAnnouncementsPage = lazy(() => import("@/pages/config/announcements"));
  const RefundsPage = lazy(() => import("@/pages/refunds/page"));
  const ReconciliationPage = lazy(() => import("@/pages/reconciliation/page"));
  const RevenueLedgerPage = lazy(() => import("@/pages/revenue-ledger/page"));

  return [
    { path: "/dashboard", element: <DashboardPage /> },
    { path: "/users", element: <UsersPage /> },
    { path: "/users/:id", element: <UserDetailPage /> },
    { path: "/agents", element: <AgentsPage /> },
    { path: "/agents/:id", element: <AgentDetailPage /> },
    { path: "/settlements", element: <SettlementsPage /> },
    { path: "/settlement-records", element: <SettlementRecordsPage /> },
    { path: "/deposits", element: <DepositsPage /> },
    { path: "/payment-addresses", element: <PaymentAddressesPage /> },
    { path: "/withdrawals", element: <WithdrawalsPage /> },
    { path: "/withdrawal-history", element: <WithdrawalHistoryPage /> },
    { path: "/risk-dashboard", element: <RiskDashboardPage /> },
    { path: "/audit-logs", element: <AuditLogsPage /> },
    { path: "/config-rates", element: <ConfigRatesPage /> },
    { path: "/config-plans", element: <ConfigPlansPage /> },
    { path: "/config-ai-models", element: <ConfigAiModelsPage /> },
    { path: "/config-announcements", element: <ConfigAnnouncementsPage /> },
    { path: "/refunds", element: <RefundsPage /> },
    { path: "/reconciliation", element: <ReconciliationPage /> },
    { path: "/revenue-ledger", element: <RevenueLedgerPage /> },
  ];
}

function createUsStockRoutes(): RouteObject[] {
  const UsStockPage = lazy(() => import("@/pages/us-stock/page"));
  return [{ path: "/us-stock", element: <UsStockPage /> }];
}

function createAiRoutes(): RouteObject[] {
  const AiUsersPage = lazy(() => import("@/pages/ai/users/page"));
  const AiUserDetailPage = lazy(() => import("@/pages/ai/users/detail"));
  const AiGenerationsPage = lazy(() => import("@/pages/ai/generations/page"));
  const AiGenerationDetailPage = lazy(() => import("@/pages/ai/generations/detail"));
  const AiVipPackagesPage = lazy(() => import("@/pages/ai/vip/packages/page"));
  const AiVipSubscriptionsPage = lazy(() => import("@/pages/ai/vip/subscriptions/page"));
  const AiSubscriptionOrdersPage = lazy(() => import("@/pages/ai/subscription-orders/page"));
  const AiOrdersPage = lazy(() => import("@/pages/ai/orders/page"));
  const AiPointsLogPage = lazy(() => import("@/pages/ai/points-log/page"));
  const AiConfigPage = lazy(() => import("@/pages/ai/config/page"));
  const AiAuditImagesPage = lazy(() => import("@/pages/ai/audit/images/page"));
  const AiAuditPromptsPage = lazy(() => import("@/pages/ai/audit/prompts/page"));
  const AiAuditAgentTracePage = lazy(() => import("@/pages/ai/audit/agent-trace/page"));
  const AiUserBehaviorPage = lazy(() => import("@/pages/ai/users/behavior/page"));
  const AiReportsPage = lazy(() => import("@/pages/ai/reports/page"));

  return [
    { path: "/ai/dashboard", element: <Navigate to="/ai/reports" replace /> },
    { path: "/ai/users", element: <AiUsersPage /> },
    { path: "/ai/users/:id", element: <AiUserDetailPage /> },
    { path: "/ai/generations", element: <AiGenerationsPage /> },
    { path: "/ai/generations/:id", element: <AiGenerationDetailPage /> },
    { path: "/ai/vip/packages", element: <AiVipPackagesPage /> },
    { path: "/ai/vip/subscription-orders", element: <AiSubscriptionOrdersPage /> },
    { path: "/ai/vip/subscriptions", element: <AiVipSubscriptionsPage /> },
    { path: "/ai/orders", element: <AiOrdersPage /> },
    { path: "/ai/points-log", element: <AiPointsLogPage /> },
    { path: "/ai/config", element: <AiConfigPage /> },
    { path: "/ai/audit/images", element: <AiAuditImagesPage /> },
    { path: "/ai/audit/prompts", element: <AiAuditPromptsPage /> },
    { path: "/ai/audit/agent-trace", element: <AiAuditAgentTracePage /> },
    { path: "/ai/users/behavior", element: <AiUserBehaviorPage /> },
    { path: "/ai/reports", element: <AiReportsPage /> },
  ];
}

function createForexRoutes(): RouteObject[] {
  const ForexPage = lazy(() => import("@/pages/forex/page"));
  const ForexAgentDetailPage = lazy(() => import("@/pages/forex/components/AgentDetailPage"));
  const ForexMerchantDetailPage = lazy(() => import("@/pages/forex/components/MerchantDetailPage"));
  const ForexDryRunDetailPage = lazy(() => import("@/pages/forex/components/DryRunDetailPage"));
  const ForexReviewDetailPage = lazy(() => import("@/pages/forex/components/ReviewDetailPage"));
  const ForexMasterDetailPage = lazy(() => import("@/pages/forex/components/MasterDetailPage"));
  const ForexSubAuditDetailPage = lazy(() => import("@/pages/forex/components/SubAuditDetailPage"));
  const ForexCopyCommandDetailPage = lazy(() => import("@/pages/forex/components/CopyCommandDetailPage"));
  const ForexModelLogsDetailPage = lazy(() => import("@/pages/forex/components/ModelLogsDetailPage"));
  const ForexExecutionDetailPage = lazy(() => import("@/pages/forex/components/ExecutionDetailPage"));
  const ForexConfigDetailPage = lazy(() => import("@/pages/forex/components/ConfigDetailPage"));
  const ForexComplianceDetailPage = lazy(() => import("@/pages/forex/components/ComplianceDetailPage"));
  const ForexDataSourcesPage = lazy(() => import("@/pages/forex/components/DataSourcesPage"));
  const ForexDataSourcesDetailPage = lazy(() => import("@/pages/forex/components/DataSourcesDetailPage"));
  const ForexFollowDetailPage = lazy(() => import("@/pages/forex/components/FollowDetailPage"));
  const ForexSubDetailPage = lazy(() => import("@/pages/forex/components/SubDetailPage"));
  const ForexTerminalPage = lazy(() => import("@/pages/forex/components/TerminalPage"));
  const ForexTerminalDetailPage = lazy(() => import("@/pages/forex/components/TerminalDetailPage"));
  const ForexTerminalCreatePage = lazy(() => import("@/pages/forex/components/TerminalCreatePage"));
  const ForexSubDetailBindPage = lazy(() => import("@/pages/forex/components/SubDetailBindPage"));
  const ForexRiskConfigDetailPage = lazy(() => import("@/pages/forex/components/RiskConfigDetailPage"));
  const ForexKillSwitchDetailPage = lazy(() => import("@/pages/forex/components/KillSwitchDetailPage"));
  const ForexQueryPolicyDetailPage = lazy(() => import("@/pages/forex/components/QueryPolicyDetailPage"));
  const ForexFailureAnalysisDetailPage = lazy(() => import("@/pages/forex/components/FailureAnalysisDetailPage"));
  const ForexBusinessClosedLoopDetailPage = lazy(() => import("@/pages/forex/components/BusinessClosedLoopDetailPage"));
  const ForexTodosPage = lazy(() => import("@/pages/forex/components/TodosPage"));
  const ForexMasterBindPage = lazy(() => import("@/pages/forex/components/MasterBindPage"));
  const ForexTerminalAnomalyPage = lazy(() => import("@/pages/forex/components/TerminalAnomalyPage"));
  const ForexTerminalAnomalyDetailNewPage = lazy(() => import("@/pages/forex/components/TerminalAnomalyDetailNewPage"));
  const ForexAgentCreatePage = lazy(() => import("@/pages/forex/components/AgentCreatePage"));
  const ForexModelDetailPage = lazy(() => import("@/pages/forex/components/ModelDetailPage"));
  const ForexStrategyTemplateDetailPage = lazy(() => import("@/pages/forex/components/StrategyTemplateDetailPage"));
  const ForexStrategyTemplateListPage = lazy(() => import("@/pages/forex/components/StrategyTemplateListPage"));
  const ForexDecisionFlowTemplateDetailPage = lazy(() => import("@/pages/forex/components/DecisionFlowTemplateDetailPage"));
  const ForexDecisionTemplateListPage = lazy(() => import("@/pages/forex/components/DecisionTemplateListPage"));
  const ForexMasterCreatePage = lazy(() => import("@/pages/forex/components/MasterCreatePage"));
  const ForexModelConfigPage = lazy(() => import("@/pages/forex/components/ForexModelConfigPage"));
  const ForexSymbolConfigDetailPage = lazy(() => import("@/pages/forex/components/SymbolConfigDetailPage"));
  const ForexConfigReleasesPage = lazy(() => import("@/pages/forex/components/ConfigReleasesPage"));
  const ForexConfigReleasesDetailPage = lazy(() => import("@/pages/forex/components/ConfigReleasesDetailPage"));
  const ProfitDetailPage = lazy(() => import("@/pages/forex/components/ProfitDetailPage"));
  const ExceptionsDetailPage = lazy(() => import("@/pages/forex/components/ExceptionsDetailPage"));
  const ProfitDashboardPage = lazy(() => import("@/pages/forex/ProfitDashboardPage"));
  const ModelDashboardPage = lazy(() => import("@/pages/forex/ModelDashboardPage"));
  const ExceptionsPage = lazy(() => import("@/pages/forex/ExceptionsPage"));
  const AgentsAuditPage = lazy(() => import("@/pages/forex/AgentsAuditPage"));
  const MasterAccountsPage = lazy(() => import("@/pages/forex/MasterAccountsPage"));
  const MasterAccountsBindPage = lazy(() => import("@/pages/forex/MasterAccountsBindPage"));
  const TerminalAnomaliesPage = lazy(() => import("@/pages/forex/TerminalAnomaliesPage"));
  const SubAccountsPage = lazy(() => import("@/pages/forex/SubAccountsPage"));
  const SubAccountsDetailPage = lazy(() => import("@/pages/forex/SubAccountsDetailPage"));
  const MerchantsPage = lazy(() => import("@/pages/forex/MerchantsPage"));

  return [
    { path: "/forex", element: <Navigate to="/forex/overview" replace /> },
    { path: "/forex/overview", element: <ForexPage /> },
    { path: "/forex/performance", element: <ForexPage /> },
    { path: "/forex/model-usage", element: <ForexPage /> },
    { path: "/forex/anomalies", element: <ForexPage /> },
    { path: "/forex/agents/detail", element: <ForexAgentDetailPage /> },
    { path: "/forex/agents/dry-run/detail", element: <ForexDryRunDetailPage /> },
    { path: "/forex/agents/review/detail", element: <ForexReviewDetailPage /> },
    { path: "/forex/master/detail", element: <ForexMasterDetailPage /> },
    { path: "/forex/master/bind", element: <ForexPage /> },
    { path: "/forex/master/add", element: <Navigate to="/forex/master/bind" replace /> },
    { path: "/forex/merchants/detail", element: <ForexMerchantDetailPage /> },
    { path: "/forex/terminal/create", element: <ForexTerminalCreatePage /> },
    { path: "/forex/terminal/detail", element: <ForexTerminalDetailPage /> },
    { path: "/forex/terminal/anomalies/detail", element: <ForexFailureAnalysisDetailPage /> },
    { path: "/forex/sub-audit/detail", element: <ForexSubAuditDetailPage /> },
    { path: "/forex/sub-detail/bind", element: <ForexSubDetailBindPage /> },
    { path: "/forex/sub-detail/detail", element: <ForexSubDetailPage /> },
    { path: "/forex/follow/detail", element: <ForexFollowDetailPage /> },
    { path: "/forex/copycmd/detail", element: <ForexCopyCommandDetailPage /> },
    { path: "/forex/execution-detail/detail", element: <ForexExecutionDetailPage /> },
    { path: "/forex/config/detail", element: <ForexConfigDetailPage /> },
    { path: "/forex/compliance/detail", element: <ForexComplianceDetailPage /> },
    { path: "/forex/model-logs/detail", element: <ForexModelLogsDetailPage /> },
    { path: "/forex/data-sources/detail", element: <ForexDataSourcesDetailPage /> },
    { path: "/forex/risk-config/detail", element: <ForexRiskConfigDetailPage /> },
    { path: "/forex/kill-switch/detail", element: <ForexKillSwitchDetailPage /> },
    { path: "/forex/query-policy/detail", element: <ForexQueryPolicyDetailPage /> },
    { path: "/forex/failure-analysis/detail", element: <ForexFailureAnalysisDetailPage /> },
    { path: "/forex/bc-loop/detail", element: <ForexBusinessClosedLoopDetailPage /> },
    { path: "/forex/todos", element: <ForexTodosPage /> },
    { path: "/forex/master-bind", element: <ForexMasterBindPage /> },
    { path: "/forex/master/create", element: <ForexMasterCreatePage /> },
    { path: "/forex/terminal-anomaly", element: <ForexTerminalAnomalyPage /> },
    { path: "/forex/terminal-anomaly/detail", element: <ForexTerminalAnomalyDetailNewPage /> },
    { path: "/forex/strategy-templates", element: <ForexStrategyTemplateListPage /> },
    { path: "/forex/decision-templates", element: <ForexDecisionTemplateListPage /> },
    { path: "/forex/strategy-template/detail", element: <ForexStrategyTemplateDetailPage /> },
    { path: "/forex/decision-flow/detail", element: <ForexDecisionFlowTemplateDetailPage /> },
    { path: "/forex/agents/model-config/detail", element: <ForexModelDetailPage /> },
    { path: "/forex/symbol-config/detail", element: <ForexSymbolConfigDetailPage /> },
    { path: "/forex/config/releases", element: <ForexConfigReleasesPage /> },
    { path: "/forex/config/releases/detail", element: <ForexConfigReleasesDetailPage /> },
    { path: "/forex/profit-dashboard/agent-detail", element: <ProfitDetailPage /> },
    { path: "/forex/profit-dashboard/merchant-detail", element: <ProfitDetailPage /> },
    { path: "/forex/profit-dashboard/master-account-detail", element: <ProfitDetailPage /> },
    { path: "/forex/exceptions/detail", element: <ExceptionsDetailPage /> },
    { path: "/forex/profit-dashboard", element: <ProfitDashboardPage /> },
    { path: "/forex/model-dashboard", element: <ModelDashboardPage /> },
    { path: "/forex/exceptions", element: <ExceptionsPage /> },
    { path: "/forex/agents/audit", element: <AgentsAuditPage /> },
    { path: "/forex/master-accounts", element: <MasterAccountsPage /> },
    { path: "/forex/master-accounts/bind", element: <MasterAccountsBindPage /> },
    { path: "/forex/terminal-anomalies", element: <TerminalAnomaliesPage /> },
    { path: "/forex/sub-accounts", element: <SubAccountsPage /> },
    { path: "/forex/sub-accounts/detail", element: <SubAccountsDetailPage /> },
    { path: "/forex/merchants", element: <MerchantsPage /> },
    { path: "/forex/agents/create", element: <ForexAgentCreatePage /> },
    { path: "/forex/agents/review", element: <ForexPage /> },
    { path: "/forex/agents/dry-run", element: <ForexPage /> },
    { path: "/forex/agents/model-config", element: <Navigate to="/forex/model-config" replace /> },
    { path: "/forex/agents", element: <ForexPage /> },
    { path: "/forex/model-config", element: <ForexModelConfigPage /> },
    { path: "/forex/master", element: <ForexPage /> },
    { path: "/forex/terminal", element: <ForexTerminalPage /> },
    { path: "/forex/terminals", element: <Navigate to="/forex/terminal" replace /> },
    { path: "/forex/terminal/anomalies", element: <TerminalAnomaliesPage /> },
    { path: "/forex/sub-audit", element: <Navigate to="/forex/sub-accounts" replace /> },
    { path: "/forex/sub-detail", element: <Navigate to="/forex/sub-accounts" replace /> },
    { path: "/forex/follow", element: <ForexPage /> },
    { path: "/forex/follow-detail", element: <Navigate to="/forex/follow" replace /> },
    { path: "/forex/copycmd", element: <ForexPage /> },
    { path: "/forex/execution-detail", element: <ForexPage /> },
    { path: "/forex/failure-analysis", element: <ForexPage /> },
    { path: "/forex/risk-config", element: <ForexPage /> },
    { path: "/forex/kill-switch", element: <ForexPage /> },
    { path: "/forex/config", element: <ForexPage /> },
    { path: "/forex/compliance", element: <ForexPage /> },
    { path: "/forex/query-policy", element: <ForexPage /> },
    { path: "/forex/bc-loop", element: <ForexPage /> },
    { path: "/forex/data-sources", element: <ForexDataSourcesPage /> },
    { path: "/forex/admin-users", element: <ForexPage /> },
    { path: "/forex/roles", element: <ForexPage /> },
    { path: "/forex/audit", element: <ForexPage /> },
    { path: "/forex/security", element: <ForexPage /> },
    { path: "/forex/model-logs", element: <ForexPage /> },
  ];
}

const domainRoutes: RouteObject[] = [];

if (__DOMAIN_CRYPTO__) {
  domainRoutes.push(...createCryptoRoutes());
}

if (__DOMAIN_US_STOCK__) {
  domainRoutes.push(...createUsStockRoutes());
}

if (__DOMAIN_FOREX__) {
  domainRoutes.push(...createForexRoutes());
}

if (__DOMAIN_AI__) {
  domainRoutes.push(...createAiRoutes());
}

const routes: RouteObject[] = [
  { path: "/login", element: <LoginPage /> },
  {
    element: <AuthGuard />,
    children: [
      { path: "/", element: <OnboardingPage /> },
      {
        element: <AdminUsersLayout />,
        children: [
          { path: "/admin-users", element: <AdminUsersPage /> },
          { path: "/admin-users/:id", element: <AdminUserDetailPage /> },
          { path: "/system-config", element: <SystemConfigPage /> },
        ],
      },
      {
        element: <AdminLayout />,
        children: domainRoutes,
      },
    ],
  },
  { path: "*", element: <NotFound /> },
];

export default routes;
